// src/components/StudentFeedbackCard.jsx
//
// PLAN S11d (frontend half) / S12: a small, non-blocking feedback card.
// Polls GET /tool-feedback/pending (S11d backend, G70 option b) once on
// mount; if a tool is pending, shows 1-5 stars, one optional reason chip,
// an optional 300-char comment, and Skip (D8). Submit/skip both use the
// existing S10 endpoints. Silent on any network error either way - never
// blocks the rest of the dashboard.
import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { baseUrl } from '../config';

const TOOL_LABELS = {
  script: 'Script Analysis',
  budget: 'Budgeting',
  casting: 'Casting Assistant',
  location: 'Location Scouting',
};
const REASONS = ['Fast', 'Accurate', 'Confusing', 'Slow', 'Buggy'];
const COMMENT_MAX_LEN = 300;

const StudentFeedbackCard = () => {
  const [toolKey, setToolKey] = useState(null);
  const [rating, setRating] = useState(0);
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/tool-feedback/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.tool_key) setToolKey(data.tool_key);
      } catch (_) {
        // Silent - the card just doesn't show.
      }
    })();
  }, []);

  const answer = async (path, body) => {
    const token = localStorage.getItem('access_token');
    try {
      await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
    } catch (_) {
      // Silent either way - the card is non-blocking, per D8/S12.
    }
    setDismissed(true);
  };

  const handleSkip = () => answer('/tool-feedback/skip', { tool_key: toolKey });
  const handleSubmit = () => answer('/tool-feedback/submit', {
    tool_key: toolKey, rating, reason, comment: comment || undefined,
  });

  if (!toolKey || dismissed) return null;

  return (
    <div className="rounded-2xl border border-teal-200 bg-white p-6 mb-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-800">
            How was {TOOL_LABELS[toolKey] || 'this tool'}?
          </p>
          <p className="text-sm text-slate-500 mt-0.5">A quick rating helps us improve it.</p>
        </div>
        <button onClick={handleSkip} className="text-sm text-slate-400 hover:text-slate-600">
          Skip
        </button>
      </div>

      <div className="flex gap-1 mt-4" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            className="p-0.5"
          >
            <Star size={22} className={n <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {REASONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setReason(r === reason ? null : r)}
            className={
              r === reason
                ? 'text-xs font-medium px-3 py-1.5 rounded-full border bg-teal-600 text-white border-teal-600'
                : 'text-xs font-medium px-3 py-1.5 rounded-full border bg-white text-slate-600 border-gray-300 hover:border-teal-400'
            }
          >
            {r}
          </button>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX_LEN))}
        placeholder="Anything else? (optional)"
        maxLength={COMMENT_MAX_LEN}
        rows={2}
        className="w-full mt-3 rounded-lg border border-gray-300 p-2 text-sm text-slate-700 focus:border-teal-400 focus:outline-none"
      />

      <div className="flex justify-end mt-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={rating === 0}
          className="text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full px-5 py-2 transition-colors"
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default StudentFeedbackCard;
